/**
 * Internacionalizacao (spec 18.5).
 *
 * Toda label, menu, status, tipo, prioridade, relacao, mensagem e texto de ajuda
 * e traduzivel. Conteudo escrito pelo usuario (titulo, descricao, comentario)
 * NAO e traduzido automaticamente nesta versao — fica no idioma de cadastro.
 */

/** Codigo BCP 47. */
export type LocaleCode = string

export const DEFAULT_LOCALE = 'pt-BR'

/**
 * Cadeia de fallback: idioma do usuario -> idioma padrao da empresa -> pt-BR
 * -> a propria chave tecnica. A chave tecnica aparecendo na tela e o sinal de
 * traducao faltando; nunca renderize string vazia.
 */
export function resolveLocaleChain(userLocale: LocaleCode | null, companyLocale: LocaleCode | null): LocaleCode[] {
  const chain: LocaleCode[] = []
  for (const locale of [userLocale, companyLocale, DEFAULT_LOCALE]) {
    if (locale && !chain.includes(locale)) chain.push(locale)
  }
  return chain
}

export type TranslationBundle = Readonly<Record<string, string>>

/** Traducoes indexadas por locale. */
export type TranslationCatalog = Readonly<Record<LocaleCode, TranslationBundle>>

export type Translator = (key: string, vars?: Readonly<Record<string, string | number>>) => string

/**
 * Cria um tradutor que percorre a cadeia de fallback e interpola `{{var}}`.
 * Retorna a propria chave quando nenhum locale da cadeia tem o termo.
 */
export function createTranslator(catalog: TranslationCatalog, chain: readonly LocaleCode[]): Translator {
  return (key, vars) => {
    let value: string | undefined
    for (const locale of chain) {
      const found = catalog[locale]?.[key]
      if (found !== undefined) {
        value = found
        break
      }
    }
    if (value === undefined) return key
    if (!vars) return value
    return value.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, name: string) => {
      const replacement = vars[name]
      return replacement === undefined ? match : String(replacement)
    })
  }
}

/**
 * Chaves obrigatorias da entidade central. A spec 18.5 exige explicitamente que
 * pt-BR resolva para "Item de Trabalho" / "Itens de Trabalho".
 */
export const ENTITY_KEYS = {
  workItemSingular: 'entity.work_item.singular',
  workItemPlural: 'entity.work_item.plural',
  demandSingular: 'entity.demand.singular',
  demandPlural: 'entity.demand.plural',
  portfolioNodeSingular: 'entity.portfolio_node.singular',
} as const

/** Cobertura de traducao de um locale, para o grid de Traducoes do admin. */
export function translationCoverage(catalog: TranslationCatalog, locale: LocaleCode, allKeys: readonly string[]) {
  const bundle = catalog[locale] ?? {}
  const missing = allKeys.filter((k) => bundle[k] === undefined)
  return {
    total: allKeys.length,
    translated: allKeys.length - missing.length,
    missing,
    ratio: allKeys.length === 0 ? 1 : (allKeys.length - missing.length) / allKeys.length,
  }
}
